import { useState, useRef, useEffect, useCallback } from 'react';
import { usePointerLock } from '../hooks/usePointerLock';
import { useAssetManager } from './AssetManager';
import { useSceneInteraction } from '../hooks/useSceneInteraction';
import { CAMERA_CONFIG } from '../config/sceneConfig';
import { SceneControls } from '../components/SceneControls';

export const useSceneManager = ({ onSpotSelect, assets, isViewMode: propIsViewMode, initialFov }) => {
    const controlsRef = useRef();
    const sceneContainerRef = useRef();

    const { isLocked, handleLock, handleUnlock, forceUnlock } = usePointerLock(controlsRef);
    const {
        assets: managedAssets,
        loadingStates,
        selectedAsset,
        loadAsset,
        updateAsset,
        removeAsset,
        selectAsset,
        ASSET_TYPES
    } = useAssetManager();

    const {
        canInteract,
        selectedSpot,
        isShowMode,
        handleSpotSelect,
        toggleShowMode,
        setCanInteract,
        setSelectedSpot
    } = useSceneInteraction(onSpotSelect, forceUnlock);
    
    const [fov, setFov] = useState(initialFov || CAMERA_CONFIG.defaultFOV);

    // Use either prop or internal show mode
    const effectiveViewMode = propIsViewMode || isShowMode;

    // Define handleViewModeToggle before using it in useEffect
    const handleViewModeToggle = useCallback(() => {
        console.log('Toggle view mode', { isShowMode, isLocked }); // Debug log
        
        if (!isShowMode) {
            // Entering show mode
            toggleShowMode();
            
            // Request pointer lock and enable movement
            if (controlsRef.current?.movementSystem) {
                const movementSystem = controlsRef.current.movementSystem;
                movementSystem.enable();  // Enable first
                requestAnimationFrame(() => {
                    movementSystem.lock();  // Then request lock
                    console.log('Requested lock in view mode toggle');
                });
            }
        } else {
            // Exiting show mode
            toggleShowMode();
            forceUnlock();
        }
    }, [isShowMode, toggleShowMode, forceUnlock]);

    // Add V key handler after handleViewModeToggle is defined
    useEffect(() => {
        const handleKeyPress = (event) => {
            if (event.code === 'KeyV' && !propIsViewMode) { // Prevent toggle if in prop-based view mode
                handleViewModeToggle();
            }
        };

        document.addEventListener('keydown', handleKeyPress);
        return () => document.removeEventListener('keydown', handleKeyPress);
    }, [handleViewModeToggle, propIsViewMode]);

    // Auto-enable controls in view mode (but don't auto-lock immediately)
    useEffect(() => {
        if (effectiveViewMode && controlsRef.current?.movementSystem) {
            const movementSystem = controlsRef.current.movementSystem;
            movementSystem.enable();
            console.log('Auto-enabled controls in view mode');
            // Remove auto-lock - let user click to engage when ready
        }
    }, [effectiveViewMode]);

    // Handle window click to engage controls in view mode (only when user clicks)
    useEffect(() => {
        const handleWindowClick = (event) => {
            // Only lock if clicking on the canvas/scene area, not UI elements
            if (effectiveViewMode && controlsRef.current?.movementSystem && !isLocked) {
                const target = event.target;
                const isCanvasClick = target.tagName === 'CANVAS' || target.closest('canvas');
                
                if (isCanvasClick) {
                    const movementSystem = controlsRef.current.movementSystem;
                    movementSystem.enable();
                    requestAnimationFrame(() => {
                        movementSystem.lock();
                        console.log('Engaged controls on canvas click');
                    });
                }
            }
        };

        window.addEventListener('click', handleWindowClick);
        return () => window.removeEventListener('click', handleWindowClick);
    }, [effectiveViewMode, isLocked]);

    // Rest of the effects...
    useEffect(() => {
        if (selectedSpot) {
            forceUnlock();
        }
    }, [selectedSpot, forceUnlock]);

    useEffect(() => {
        if (assets) {
            Object.entries(assets).forEach(([id, asset]) => {
                if (asset && asset.url) {
                    loadAsset(id, asset.url,
                        asset.url.toLowerCase().endsWith('.glb') ? ASSET_TYPES.MODEL : ASSET_TYPES.IMAGE
                    );
                }
            });
        }
    }, [assets, loadAsset]);

    const handleSpotSelection = useCallback((spotId, isEditMode) => {
        selectAsset(spotId);
        handleSpotSelect(spotId, isEditMode);
        onSpotSelect(spotId, isEditMode);
    }, [selectAsset, handleSpotSelect, onSpotSelect]);

    const handleAssetUpdate = useCallback((id, changes) => {
        updateAsset(id, changes);
    }, [updateAsset]);

    return {
        controlsRef,
        sceneContainerRef,
        isLocked,
        handleLock,
        handleUnlock,
        managedAssets,
        loadingStates,
        selectedAsset,
        loadAsset,
        updateAsset: handleAssetUpdate,
        removeAsset,
        canInteract,
        selectedSpot,
        isShowMode,
        handleSpotSelect,
        fov,
        setFov,
        effectiveViewMode,
        handleSpotSelection,
        handleViewModeToggle,
        setSelectedSpot,
        setCanInteract
    };
};

